import { useEffect, useMemo, useState } from "react";
import { ClipboardCheck, AlertCircle, FileDown, FileText,
         ArrowDown, ArrowUp, Search, CheckCircle2, TrendingUp, Timer } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";

const ACCENT = "#A78BFA"; // GFRC purple

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString();
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "2-digit" }); }
  catch { return iso; }
}
function gapColor(gap, threshold) {
  if (gap === null || gap === undefined) return "text-muted-foreground";
  if (gap <= threshold) return "text-emerald-400";
  if (gap <= threshold * 2) return "text-amber-400";
  return "text-red-400";
}

const STATUS_BADGE = {
  PASS: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  UNCHECKED: "bg-amber-500/10 border-amber-500/30 text-amber-400",
};

export default function GfrcQcPipelineCard({ showMexico = false }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [sortBy, setSortBy] = useState("gap");
  const [sort, setSort] = useState("desc");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ rows: [], summary: {}, categories: [], gap_threshold: 1 });
  const [loading, setLoading] = useState(false);
  const [interacted, setInteracted] = useState(false);

  const toggleSort = (key) => {
    setInteracted(true);
    setSortBy(key);
    setSort((prev) => (sortBy === key ? (prev === "desc" ? "asc" : "desc") : (key === "cast" ? "asc" : "desc")));
  };

  const params = useMemo(() => {
    const p = { preset: filter.preset, sort_by: sortBy, sort, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (status)   p.status = status;
    if (category) p.category = category;
    return p;
  }, [filter, status, category, sortBy, sort, showMexico]);

  useEffect(() => {
    if (!interacted) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.get("/departments/GFRC/qc-pipeline/pipeline-rows", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params, interacted]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return data.rows || [];
    const q = search.toLowerCase();
    return (data.rows || []).filter((r) =>
      (r.job_name || "").toLowerCase().includes(q) ||
      (r.piece_mark || "").toLowerCase().includes(q) ||
      (r.piece_sn || "").toLowerCase().includes(q)
    );
  }, [data.rows, search]);

  const summary = data.summary || {};
  const threshold = data.gap_threshold ?? 1;

  const handleExport = (fmt) => {
    const qs = new URLSearchParams({ fmt, preset: filter.preset, show_mexico: String(showMexico), sort_by: sortBy, sort });
    if (filter.preset === "custom") { qs.set("start", filter.start); qs.set("end", filter.end); }
    if (status)   qs.set("status", status);
    if (category) qs.set("category", category);
    if (search && search.trim()) qs.set("search", search.trim());
    const token = localStorage.getItem("token");
    if (token) qs.set("token", token);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/departments/GFRC/qc-pipeline/export?${qs.toString()}`;
  };

  const onFilterChange = (v) => { setInteracted(true); setFilter(v); };
  const onStatusChange = (v) => { setInteracted(true); setStatus(v); };
  const onCategoryChange = (v) => { setInteracted(true); setCategory(v); };
  const quickLoad = () => { setInteracted(true); setFilter({ preset: "last30" }); };

  return (
    <section className="relative bg-[#12141D] border border-border overflow-hidden" data-testid="gfrc-qc-pipeline">
      <span className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      {/* Header row */}
      <div className="px-5 py-4 border-b border-border flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={18} style={{ color: ACCENT }} />
            <h3 className="font-heading font-bold text-xl">GFRC QC PIPELINE</h3>
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              · QC Pipeline · {summary.total ?? 0} pieces
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
              <span className="px-2 py-1 border border-border bg-[#0A0B10]">Coverage</span>
              <span className="px-2 py-1 text-[#0A0B10] font-mono font-semibold"
                style={{ backgroundColor: ACCENT }} data-testid="gfrc-qc-coverage">
                {summary.coverage_days ?? 0}d
              </span>
            </div>
            <button data-testid="gfrc-qc-csv" onClick={() => handleExport("csv")}
              className="inline-flex items-center gap-1 px-2 py-1 border border-border bg-[#0A0B10] hover:border-[#A78BFA]/60 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white transition-colors">
              <FileDown size={11} /> CSV
            </button>
            <button data-testid="gfrc-qc-pdf" onClick={() => handleExport("pdf")}
              className="inline-flex items-center gap-1 px-2 py-1 border border-border bg-[#0A0B10] hover:border-[#A78BFA]/60 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white transition-colors">
              <FileText size={11} /> PDF
            </button>
          </div>
        </div>

        <DateFilter value={filter} onChange={onFilterChange} />
      </div>

      {/* KPI mini-tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 px-5 py-4 border-b border-border">
        <MiniTile testid="gfrc-qc-total"    label="Total Pieces" value={fmtNum(summary.total ?? 0)} accent={ACCENT} />
        <MiniTile testid="gfrc-qc-passed"   label="Passed"        value={fmtNum(summary.passed ?? 0)} accent="#10B981" />
        <MiniTile testid="gfrc-qc-unchecked" label="Unchecked"    value={fmtNum(summary.unchecked ?? 0)} accent="#FBBF24" />
        <MiniTile testid="gfrc-qc-flagged"  label="Flagged"       value={fmtNum(summary.flagged ?? 0)}
          accent="#F87171" sub={`gap > ${threshold} day${threshold === 1 ? "" : "s"}`}
          icon={<AlertCircle size={11} />} />
        <MiniTile testid="gfrc-qc-pass-rate" label="Pass Rate" value={`${(summary.pass_rate ?? 0).toFixed(1)}%`} accent="#10B981" icon={<CheckCircle2 size={11} />} />
        <MiniTile testid="gfrc-qc-avg-days"  label="Avg Days"  value={fmtNum(summary.avg_gap_days ?? 0)} accent="#00D4FF" icon={<TrendingUp size={11} />} />
        <MiniTile testid="gfrc-qc-max-days"  label="Max Days"  value={fmtNum(summary.max_gap_days ?? 0)} accent="#F87171" icon={<Timer size={11} />} />
      </div>

      {/* Filter row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-5 py-4 border-b border-border">
        <div className="relative">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="gfrc-qc-search" value={search} onChange={(e) => { setInteracted(true); setSearch(e.target.value); }}
            placeholder="Search job / piece…"
            className="w-full bg-[#0A0B10] border border-border pl-8 pr-3 py-2 text-[12px] text-white focus:outline-none focus:border-[#A78BFA] transition-colors" />
        </div>
        <select data-testid="gfrc-qc-status-filter" value={status} onChange={(e) => onStatusChange(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-2 text-[12px] text-white cursor-pointer focus:outline-none focus:border-[#A78BFA] transition-colors">
          <option value="">All Status</option>
          <option value="PASS">Pass</option>
          <option value="UNCHECKED">Unchecked</option>
        </select>
        <select data-testid="gfrc-qc-category-filter" value={category} onChange={(e) => onCategoryChange(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-2 text-[12px] text-white cursor-pointer focus:outline-none focus:border-[#A78BFA] transition-colors">
          <option value="">All Categories</option>
          {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Rows */}
      {!interacted && (
        <div className="p-8 text-center" data-testid="gfrc-qc-empty">
          <ClipboardCheck size={32} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Select a filter or date range to load pieces.</p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
            Use the date range, status, or category filters above
          </p>
          <button data-testid="gfrc-qc-quick-load" onClick={quickLoad}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest font-mono"
            style={{ backgroundColor: ACCENT, color: "#0A0B10" }}>
            Quick Load · Last 30 Days →
          </button>
        </div>
      )}
      {loading && (
        <div className="p-10 text-center text-sm uppercase tracking-widest text-muted-foreground">Loading…</div>
      )}
      {!loading && filteredRows.length === 0 && (
        <div className="p-10 text-center text-sm uppercase tracking-widest text-muted-foreground">No pieces match filters</div>
      )}
      {!loading && filteredRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="py-2 px-4">
                  <SortHeader label="Cast" sortKey="cast" active={sortBy} dir={sort} onClick={toggleSort} testid="gfrc-qc-th-cast" accent={ACCENT} />
                </th>
                <th className="py-2 px-4">Check</th>
                <th className="py-2 px-4">Job</th>
                <th className="py-2 px-4">Piece</th>
                <th className="py-2 px-4">Category</th>
                <th className="py-2 px-4 text-right">SF</th>
                <th className="py-2 px-4 text-right">
                  <SortHeader label="Gap" sortKey="gap" active={sortBy} dir={sort} onClick={toggleSort} align="right" testid="gfrc-qc-th-gap" accent={ACCENT} />
                </th>
                <th className="py-2 px-4">Status</th>
              </tr>
            </thead>
            <tbody data-testid="gfrc-qc-rows">
              {filteredRows.slice(0, 400).map((r, i) => (
                <tr key={i} className="border-b border-border/40 hover:bg-[#0A0B10]/40 transition-colors">
                  <td className="py-2 px-4 font-mono text-muted-foreground">{fmtDate(r.cast_date)}</td>
                  <td className="py-2 px-4 font-mono text-muted-foreground">{fmtDate(r.check_date)}</td>
                  <td className="py-2 px-4">{r.job_name || "—"}</td>
                  <td className="py-2 px-4 font-mono">
                    <div>{r.piece_mark || "—"}</div>
                    {r.piece_sn && <div className="text-[10px] text-muted-foreground">SN: {r.piece_sn}</div>}
                  </td>
                  <td className="py-2 px-4 text-muted-foreground">{r.category || "—"}</td>
                  <td className="py-2 px-4 font-mono text-right">{fmtNum(r.total_sf)}</td>
                  <td className={`py-2 px-4 font-mono text-right font-semibold ${gapColor(r.detection_gap_days, threshold)}`}>
                    {r.detection_gap_days === null || r.detection_gap_days === undefined ? "—" : `${r.detection_gap_days}d`}
                  </td>
                  <td className="py-2 px-4">
                    <span className={`inline-block px-2 py-0.5 border text-[10px] uppercase tracking-widest ${STATUS_BADGE[r.status] || "border-border text-muted-foreground"}`}>
                      {r.status || "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MiniTile({ testid, label, value, accent, sub, icon }) {
  return (
    <div className="bg-[#0A0B10] border border-border p-3 relative overflow-hidden" data-testid={testid}>
      <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: accent }} />
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
        {icon && <span style={{ color: accent }}>{icon}</span>}
        <span>{label}</span>
      </div>
      <div className="font-heading font-bold text-2xl tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function SortHeader({ label, sortKey, active, dir, onClick, align, testid, accent = "#00D4FF" }) {
  const isActive = active === sortKey;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onClick(sortKey)}
      className={`inline-flex items-center gap-1 uppercase tracking-widest text-[10px] transition-colors ${
        isActive ? "text-white" : "text-muted-foreground hover:text-white"
      } ${align === "right" ? "justify-end w-full" : ""}`}
    >
      <span>{label}</span>
      {isActive ? (
        dir === "desc"
          ? <ArrowDown size={11} style={{ color: accent }} />
          : <ArrowUp size={11} style={{ color: accent }} />
      ) : (
        <ArrowDown size={11} className="opacity-30" />
      )}
    </button>
  );
}
